import { useCurrTimeState } from "@/models/time-control/timeStateHooks";
import { TimeStates } from "@/models/time-control/timeStateTypes";
import { PrefetchPool } from "@/prefetch-controller/PrefetchPool";
import {
  DEFAULT_ANIMATION_PREFETCH_INITIAL_FRAMES,
  markAnimationPrefetchFramesLoading,
  markAnimationPrefetchFramesReady,
  resetAnimationPrefetchProgress,
  startAnimationPrefetchRun,
} from "@/reducer/PrefetchState";
import { useActiveLayerPropsPrefetchState, useViewportPrefetchState } from "@/reducer/selectors/PrefetchStateSelectors";
import { setTimeList } from "@/time";
import { DateTime, Interval } from "luxon";
import { type ReactNode, useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";

type PrefetchControllerProps = {
  children?: ReactNode;
};

/**
 * A component that triggers the prefetcher pool at the right timing
 * (layer change, time state change, viewport change etc)
 */
export function PrefetchController({ children }: PrefetchControllerProps) {
  const dispatch = useDispatch();
  const viewportChanged = useViewportPrefetchState();
  const activeLayersPropsChanged = useActiveLayerPropsPrefetchState();
  const timeState = useCurrTimeState();
  const isAnimationState = timeState.matches(TimeStates.ANIMATION);
  const { temporalResolution, startTime, endTime } = isAnimationState
    ? timeState.context.modeOpts
    : { temporalResolution: null, startTime: null, endTime: null };
  const isAnimationPaused = isAnimationState ? timeState.context.modeOpts.isPaused : true;
  const displayTime = timeState.context.displayTime;
  const nextDisplayTime = timeState.context.nextDisplayTime_;
  const hasPendingTimeTransition = +displayTime !== +nextDisplayTime;
  const initialFrames = DEFAULT_ANIMATION_PREFETCH_INITIAL_FRAMES;
  const animationWindowSignature =
    isAnimationState && startTime && endTime && temporalResolution
      ? `${startTime.toMillis()}:${endTime.toMillis()}:${temporalResolution.toMillis()}`
      : null;
  const previousAnimationWindowSignatureRef = useRef<string | null>(null);
  const didAnimationWindowChange =
    animationWindowSignature !== null &&
    previousAnimationWindowSignatureRef.current !== null &&
    previousAnimationWindowSignatureRef.current !== animationWindowSignature;
  // Derive prefetch mode from time-state only:
  // - running animation => fill forward
  // - changed animation window => reseed from current anchor
  // - paused but user staged a next time => fill from staged target
  const effectiveMode: "seed_only" | "fill_remaining" = !isAnimationPaused
    ? "fill_remaining"
    : didAnimationWindowChange
      ? "seed_only"
      : hasPendingTimeTransition
        ? "fill_remaining"
        : "seed_only";
  const anchorTimestampMs = (
    !isAnimationPaused ? displayTime : hasPendingTimeTransition ? nextDisplayTime : displayTime
  ).toMillis();
  // Anchor follows what the user actually sees (or staged next frame when paused)
  // so prefetch work stays aligned with scrubbing/step operations.

  // Debounce configuration (in milliseconds)
  const DEBOUNCE_MS = 1000; // 1s: trigger only after user stops interacting

  // Compute whether we should trigger prefetch at all
  const shouldPrefetch =
    isAnimationState &&
    !!activeLayersPropsChanged &&
    !!viewportChanged &&
    !!startTime &&
    !!endTime &&
    !!temporalResolution;

  const structuralPrefetchData = useMemo(() => {
    if (!shouldPrefetch || !startTime || !endTime || !temporalResolution) {
      return null;
    }

    const interval = Interval.fromDateTimes(startTime, endTime);
    const frameTimestampsMs = setTimeList(interval, temporalResolution).map((time) => time.toMillis());

    return {
      interval,
      frameTimestampsMs,
      signature: JSON.stringify({
        frameTimestampsMs,
        temporalResolutionMs: temporalResolution.toMillis(),
        viewportChanged,
        activeLayersPropsChanged,
        initialFrames,
      }),
    };
  }, [
    shouldPrefetch,
    startTime,
    endTime,
    temporalResolution,
    viewportChanged,
    activeLayersPropsChanged,
    initialFrames,
  ]);

  // Keep references for the pool instance and its active structural signature
  const poolRef = useRef<PrefetchPool | null>(null);
  const prefetchRunIdRef = useRef(0);
  const currentPoolSignatureRef = useRef<string | null>(null);
  const livePoolInputsRef = useRef({
    anchorTimestampMs,
    displayTime,
    effectiveMode,
  });

  livePoolInputsRef.current = {
    anchorTimestampMs,
    displayTime,
    effectiveMode,
  };

  useEffect(() => {
    previousAnimationWindowSignatureRef.current = animationWindowSignature;
  }, [animationWindowSignature]);

  /**
   * Dispose stale work immediately when animation mode exits or the structural
   * prefetch space changes, so old progress cannot leak into the next run.
   */
  useEffect(() => {
    if (!shouldPrefetch || !structuralPrefetchData) {
      if (poolRef.current) {
        poolRef.current.dispose();
        poolRef.current = null;
      }
      currentPoolSignatureRef.current = null;
      dispatch(resetAnimationPrefetchProgress());
      return;
    }
    if (currentPoolSignatureRef.current && currentPoolSignatureRef.current !== structuralPrefetchData.signature) {
      poolRef.current?.dispose();
      poolRef.current = null;
      currentPoolSignatureRef.current = null;
      dispatch(resetAnimationPrefetchProgress());
    }
  }, [shouldPrefetch, structuralPrefetchData, isAnimationState, dispatch]);

  /**
   * Rebuild the pool only after structural inputs settle, so rapid viewport or
   * layer changes do not constantly recreate the prefetch run.
   */
  useEffect(() => {
    if (!shouldPrefetch || !structuralPrefetchData || !startTime || poolRef.current) {
      return;
    }

    const debounceTimer = setTimeout(() => {
      if (poolRef.current || currentPoolSignatureRef.current === structuralPrefetchData.signature) {
        return;
      }

      const runId = ++prefetchRunIdRef.current;
      const livePoolInputs = livePoolInputsRef.current;

      dispatch(
        startAnimationPrefetchRun({
          runId,
          frameTimestampsMs: structuralPrefetchData.frameTimestampsMs,
        }),
      );

      const pool = new PrefetchPool({
        interval: structuralPrefetchData.interval,
        temporalResolution,
        anchorTime: DateTime.fromMillis(livePoolInputs.anchorTimestampMs).setZone(startTime.zone),
        displayTime: livePoolInputs.displayTime,
        initialFrames,
        mode: livePoolInputs.effectiveMode,
        seekerLength: 2,
        callbacks: {
          onSliceStart: (frameTimestamps) => {
            dispatch(
              markAnimationPrefetchFramesLoading({
                runId,
                frameTimestampsMs: frameTimestamps.map((time) => time.toMillis()),
              }),
            );
          },
          onSliceReady: (frameTimestamps) => {
            dispatch(
              markAnimationPrefetchFramesReady({
                runId,
                frameTimestampsMs: frameTimestamps.map((time) => time.toMillis()),
              }),
            );
          },
        },
      });
      poolRef.current = pool;
      currentPoolSignatureRef.current = structuralPrefetchData.signature;
      pool.process();
    }, DEBOUNCE_MS);

    return () => clearTimeout(debounceTimer);
  }, [shouldPrefetch, structuralPrefetchData, startTime, temporalResolution, dispatch, initialFrames]);

  /**
   * Apply fast interaction updates directly to the active pool so playback,
   * scrubbing, and step buttons can steer prefetching without debounce.
   */
  useEffect(() => {
    if (!shouldPrefetch || !poolRef.current || !startTime || !structuralPrefetchData) {
      return;
    }
    if (currentPoolSignatureRef.current !== structuralPrefetchData.signature) {
      return;
    }

    poolRef.current.updateDisplayTime(displayTime);
    poolRef.current.updateAnchor(DateTime.fromMillis(anchorTimestampMs).setZone(startTime.zone));
    poolRef.current.updateMode(effectiveMode);

    poolRef.current.process();
  }, [shouldPrefetch, structuralPrefetchData, startTime, displayTime, anchorTimestampMs, effectiveMode, dispatch]);

  /**
   * Dispose the pool and clear prefetch state when the controller unmounts.
   */
  useEffect(() => {
    return () => {
      if (poolRef.current) {
        poolRef.current.dispose();
        poolRef.current = null;
      }
      currentPoolSignatureRef.current = null;
      dispatch(resetAnimationPrefetchProgress());
    };
  }, [dispatch]);

  return <>{children}</>;
}
